import * as CustomerCartServerRequests from "../../server/CustomerCartServerRequests";
import { LazyStore } from "../../utils/Store";
import { SavedCartActions } from "../../actions/SavedCartActions";
import { generateRequestId } from "../common-cart/CommonCartStore";
import { trackUiError } from "../../utils/TrackingUtil";
import { ErrorHandler } from "../ErrorHandler";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import type { CartContextStoreImpl } from "../common-cart/CartContextStore";
import { CustomerCartContextStore, SavedCartContextStore } from "../common-cart/CartContextStore";
import type {
	CustomerCart,
	CustomerCartContext,
	CustomerCartStatus,
	SessionContext,
} from "../../model/CustomerCartTypes";
import type { ImageSources } from "../../model/ArticleTypes";
export type CartMetadata = {
	customerId: string | null | undefined;
	cardholderNumber: string | null | undefined;
	fsdAddressId: string | null | undefined;
	storeId: string | null | undefined;
};
export type UnifiedCartItemView = {
	bundleId: string;
	imageSources: ImageSources;
	description: string;
};
export type UnifiedCartSearchStoreCartView = {
	cartId: string;
	items: Map<string, UnifiedCartItemView>;
	lastModifiedAt: number | null | undefined;
	cartName: string | null | undefined;
};
export type UnifiedCartsSearchStoreState = {
	carts: Array<UnifiedCartSearchStoreCartView>;
} & CartMetadata;
type UnifiedCartsSearchStoreInternalState = {
	context: CartsContext;
	carts: Array<CustomerCart | null | undefined>;
	deletedCartsIds: string[];
	optimisticallyAddedCart: CustomerCart | null | undefined;
};
type CartsContext = {
	country: string | null | undefined;
	locale: string | null | undefined;
} & CartMetadata;

function mapCartToCartView(cart: CustomerCart): UnifiedCartSearchStoreCartView {
	const itemsView = new Map();
	Object.keys(cart.items).forEach((key) => {
		const { bundleId, imageSources, description } = cart.items[key] || {};

		if (!imageSources) {
			return;
		}

		itemsView.set(key, {
			bundleId,
			imageSources,
			description,
		});
	});
	return {
		cartId: cart.cartId,
		items: itemsView,
		lastModifiedAt: cart.lastModifiedAt,
		cartName: cart.cartName,
	};
}

export class UnifiedCartSearchStoreImpl extends LazyStore {
	_cartContextStore: CartContextStoreImpl;
	_errorHandler: ErrorHandler = new ErrorHandler([]);
	_state!: UnifiedCartsSearchStoreInternalState;
	_mode: CustomerCartStatus;

	constructor(mode: CustomerCartStatus, cartContextStore: CartContextStoreImpl) {
		super();
		this._cartContextStore = cartContextStore;
		this._mode = mode;
		this.resetState();
	}

	onArm = () => {
		this.listenTo(this._cartContextStore, () => {
			this.handleCartContextChange().catch((error) => {
				console.error("handleCartContextChange in UnifiedCartSearchStore failed with ", error);
			});
		});
		this.listenTo(SavedCartActions.deleteSpecificCart, (cartId: string) => {
			this.deleteSavedCart(cartId).catch((error) => {
				console.error("deleteSavedCart in UnifiedCartSearchStore failed with ", error);
			});
		});
		this.listenTo(SavedCartActions.addSavedCartOptimistically, (cart: CustomerCart) => {
			this.addSavedCartOptimistically(cart);
		});
		this.handleCartContextChange().catch((error) => {
			console.error("handleCartContextChange in UnifiedCartSearchStore failed with ", error);
		});
	};
	onDisarm = () => {
		this.resetState();
	};

	resetState() {
		this._state = {
			context: {
				country: null,
				storeId: null,
				customerId: null,
				cardholderNumber: null,
				fsdAddressId: null,
				locale: null,
			},
			carts: [],
			deletedCartsIds: [],
			optimisticallyAddedCart: null,
		};
	}

	async updateCartViews(context: CartsContext) {
		const { country, storeId, customerId, cardholderNumber } = context;

		if (!country || !storeId || !customerId || !cardholderNumber) {
			this.resetState();
			return this.trigger();
		}

		const carts = await this.fetchCarts(country, storeId, customerId, cardholderNumber);
		this._state = { ...this._state, context, carts, deletedCartsIds: [] };
		return this.trigger();
	}

	async handleCartContextChange() {
		const { country, storeId, customerId, cardholderNumber, fsdAddressId, locale } =
			this._cartContextStore.getState();

		const context = {
			country,
			storeId,
			customerId,
			cardholderNumber,
			fsdAddressId,
			locale,
		};

		if (this.hasContextChanged(context)) {
			await this.updateCartViews(context);
		}
	}

	hasContextChanged({ country, storeId, customerId, cardholderNumber }: CartsContext) {
		const {
			country: stateCountry,
			storeId: stateStoreId,
			customerId: stateCustomerId,
			cardholderNumber: stateCardholderNumber,
		} = this._state.context;
		return (
			stateCountry !== country ||
			stateStoreId !== storeId ||
			stateCustomerId !== customerId ||
			stateCardholderNumber !== cardholderNumber
		);
	}

	async fetchCarts(
		country: string,
		storeId: string,
		customerId: string,
		cardholderNumber: string,
	): Promise<Array<CustomerCart | null | undefined>> {
		const status = this._mode;

		try {
			const cartViews = await CustomerCartServerRequests.getCarts({
				customerId,
				storeId,
				country,
				status,
			}).catch((error) => {
				console.error("fetchCarts error", error);
				return Promise.reject([]);
			});
			const carts = await Promise.all(
				cartViews
					.filter((cart) => Object.entries(cart.items).length && cart.cardholderNumber === cardholderNumber)
					.map((it) =>
						this.fetchSpecificCart(it.cartId, {
							customerId: it.customerId,
							cardholderNumber: "0",
							storeId: storeId,
							fsdAddressId: it.fsdAddressId || "",
							country: SessionStore.getCountry(),
							locale: SessionStore.getLocale(),
						}),
					),
			).catch((error) => {
				console.error("fetchCarts error", error);
				return Promise.reject([]);
			});
			return carts.map((it) => {
				if (!it) return it;
				const cartView = cartViews.find((view) => view.cartId === it.cartId);

				if (!cartView) {
					return it;
				} else {
					return { ...it, lastModifiedAt: cartView.lastModifiedAt, cartName: cartView.cartName };
				}
			});
		} catch (e) {
			trackUiError("UnifiedCartSearchStore", "Could not fetch carts.", e as any);
			return [];
		}
	}

	async fetchSpecificCart(
		cartId: string | null | undefined,
		sessionContext: SessionContext,
	): Promise<CustomerCart | null> {
		if (!cartId) {
			return Promise.resolve(null);
		}

		return CustomerCartServerRequests.getCart(
			{
				sessionContext: sessionContext,
				cartId,
			},
			this._mode,
		).catch((e) => {
			this._errorHandler.onError(null, e);
			return Promise.resolve(null);
		});
	}

	getCartsWithOptimisticallyAddedCart(): Array<CustomerCart> {
		const metadata = { ...this._state.context };
		const carts: Array<CustomerCart | null | undefined> = [...this._state.carts];
		const addedCart: CustomerCart | null | undefined = this._state.optimisticallyAddedCart;
		const context: CustomerCartContext | null | undefined = addedCart ? addedCart.context : null;

		if (
			addedCart &&
			context &&
			context.country === metadata.country &&
			context.storeId === metadata.storeId &&
			addedCart.customerId === metadata.customerId &&
			context.cardholderNumber === metadata.cardholderNumber &&
			carts.findIndex((cart) => (cart ? cart.cartId === addedCart.cartId : false)) === -1
		) {
			carts.unshift(addedCart);
		}

		return carts
			.filter(Boolean)
			.filter((it) => !this._state.deletedCartsIds.includes(it!.cartId)) as Array<CustomerCart>;
	}

	getState(): UnifiedCartsSearchStoreState {
		const cartViews: Array<UnifiedCartSearchStoreCartView> =
			this.getCartsWithOptimisticallyAddedCart().map(mapCartToCartView);
		const metadata: CartMetadata = { ...this._state.context };
		return {
			carts: cartViews,
			...metadata,
		};
	}

	async deleteSavedCart(cartId: string) {
		const { country, storeId, customerId, cardholderNumber, fsdAddressId, locale } = this._state.context;

		if (!country || !storeId || !customerId || !cardholderNumber || !locale || "SAVED" !== this._mode) {
			return;
		}

		const sessionContext = {
			country,
			storeId,
			customerId,
			cardholderNumber,
			fsdAddressId: fsdAddressId || null,
			locale,
		};

		try {
			await CustomerCartServerRequests.deleteCart({
				sessionContext,
				requestId: generateRequestId(),
				cartId: cartId,
			});
			const deletedCartsIds = [...this._state.deletedCartsIds, cartId];
			const addedCart = this._state.optimisticallyAddedCart
				? this._state.optimisticallyAddedCart.cartId !== cartId //NOSONAR
					? this._state.optimisticallyAddedCart
					: null
				: null;
			this._state = { ...this._state, deletedCartsIds, optimisticallyAddedCart: addedCart };
			this.trigger();
		} catch (e) {
			this._errorHandler.onError(null, e as any);
		}
	}

	addSavedCartOptimistically(savedCart: CustomerCart) {
		if ("SAVED" !== this._mode) {
			return;
		}

		this._state = { ...this._state, optimisticallyAddedCart: savedCart };
		this.trigger();
	}
}
export const SavedCartsStore = new UnifiedCartSearchStoreImpl("SAVED", SavedCartContextStore);
export const SubmittedCartsStore = new UnifiedCartSearchStoreImpl("SUBMITTED", CustomerCartContextStore);
